/** Static route tags are for non-JS crawlers. Once React starts, Helmet owns
 * these tags; unmanaged template tags otherwise survive as duplicate or stale
 * canonicals/language alternatives after client-side navigation. Static meta
 * defaults stay available until Helmet supplies the same key, and return when
 * a route omits that key. Never discard sitewide defaults indiscriminately.
 */
export function releaseStaticRouteHead(document: Document): () => void {
  document.head
    .querySelectorAll('link[rel="canonical"]:not([data-rh]), link[hreflang]:not([data-rh]), meta[property="og:url"]:not([data-rh])')
    .forEach((tag) => tag.remove());

  const defaults = [...document.head.querySelectorAll<HTMLMetaElement>('meta:not([data-rh])')];
  const key = (tag: HTMLMetaElement) =>
    tag.getAttribute("name") || tag.getAttribute("property");
  const reconcile = () => {
    const owned = new Set(
      [...document.head.querySelectorAll<HTMLMetaElement>('meta[data-rh]')].map(key),
    );
    for (const tag of defaults) {
      const tagKey = key(tag);
      // Dimensions/alt from the default image must not describe a route image.
      const overriddenImageDetail =
        (owned.has("og:image") && tagKey?.startsWith("og:image:")) ||
        (owned.has("twitter:image") && tagKey?.startsWith("twitter:image:"));
      if (owned.has(tagKey) || overriddenImageDetail) {
        if (tag.isConnected) tag.remove();
      } else if (!tag.isConnected) {
        document.head.append(tag);
      }
    }
  };
  const observer = new MutationObserver(reconcile);
  observer.observe(document.head, { childList: true, attributes: true, subtree: true });
  reconcile();
  return () => observer.disconnect();
}